/**
 * Planned quantity × numeric pipe length per mainline (SOW), and project roll-up.
 * Pipe length strings like "500 m" or "12.5" use the first number found.
 */

import { rollupWeldsQuantityFromItems } from './plannedWorkItems';

export function parseNumericPipeLength(pipeLength) {
  if (pipeLength === undefined || pipeLength === null) return null;
  const s = String(pipeLength).trim();
  if (!s) return null;
  const m = s.match(/[-+]?\d*\.?\d+/);
  if (!m) return null;
  const n = parseFloat(m[0]);
  return Number.isFinite(n) ? n : null;
}

export function getPlannedQuantity(record) {
  const items = record?.plannedWorkItems ?? record?.planned_work_items;
  if (Array.isArray(items) && items.length > 0) {
    const rolled = rollupWeldsQuantityFromItems(items);
    if (rolled != null) return rolled;
  }
  const q = record?.plannedWelds;
  if (q === undefined || q === null || q === '') return null;
  const n = Number(q);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function getMainlineExpectedLength(record) {
  const qty = getPlannedQuantity(record);
  const len = parseNumericPipeLength(record?.pipeLength);
  if (qty === null || len === null) return null;
  return qty * len;
}

/**
 * Single mainline value for project roll-up: prefers manual expectedPipeLength (API / form),
 * else planned quantity × pipeLength (same rules as displayExpectedPipeLength).
 */
export function getMainlineExpectedLengthForRollup(record) {
  if (!record) return null;
  const manual = record.expectedPipeLength;
  if (manual !== undefined && manual !== null && String(manual).trim() !== '') {
    const n = parseNumericPipeLength(manual);
    if (n !== null) return n;
  }
  return getMainlineExpectedLength(record);
}

/** Sum expected length across all mainlines (SOW rows) that have a computable value. */
export function sumProjectExpectedLength(records) {
  if (!Array.isArray(records)) return null;
  let sum = 0;
  let any = false;
  records.forEach((r) => {
    const v = getMainlineExpectedLengthForRollup(r);
    if (v !== null) {
      sum += v;
      any = true;
    }
  });
  return any ? sum : null;
}

export function formatExpectedLength(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const rounded = Math.round(value * 1000) / 1000;
  return Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toLocaleString(undefined, { maximumFractionDigits: 3 });
}

/** Optional manual expected length; when empty, falls back to planned qty × pipe length. */
export function displayExpectedPipeLength(record) {
  const manual = record?.expectedPipeLength;
  if (manual !== undefined && manual !== null && String(manual).trim() !== '') {
    return String(manual).trim();
  }
  return formatExpectedLength(getMainlineExpectedLength(record));
}
